import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { createSceneSelectionRequestId, getQuickPracticeSceneTrigger } from './services/SceneSelectionRequest';
import { createBrowserRouter, RouterProvider, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { NavigationGuardProvider } from './components/NavigationGuard';
import { Home, ImagePlus } from 'lucide-react';
import FlashCardMap from './components/FlashCardMap';
import MapNoteActions from './components/MapNoteActions';
import PracticeSession from './components/PracticeSession';
import PracticePlayerPage from './components/PracticePlayerPage';
import FlashCardForm from './components/FlashCardForm';
import LevelUpFlashCardForm from './components/LevelUpFlashCardForm';
import ReviewLevelUpDialog from './components/ReviewLevelUpDialog';
import SessionSummary from './components/SessionSummary';
import Spinner from './components/Spinner';
import ErrorMessage from './components/ErrorMessage';
import MainPage from './components/MainPage';
import AppNavigation, { showsAppNavigation, showsAppNavigationLinks } from './components/AppNavigation';
import AppLogo from './components/AppLogo';
import LoginPage from './components/LoginPage';
import AdminPage from './components/AdminPage';
import AdminSceneBrowser from './components/AdminSceneBrowser';
import AdminPresetBrowser from './components/AdminPresetBrowser';
import UserPage from './components/UserPage';
import ProtectedRoute from './components/ProtectedRoute';
import CalendarPage from './components/CalendarPage';
import TournamentPage from './components/TournamentPage';
import TournamentEntryScene from './components/TournamentEntryScene';
import TournamentOpponentIntro from './components/TournamentOpponentIntro';
import CardLevelUpgradePage from './components/CardLevelUpgradePage';
import WritingPracticePage from './components/WritingPracticePage';
import WritingResultPage from './components/WritingResultPage';
import VocabularyPage from './components/VocabularyPage';
import VocabularyPracticePage from './components/VocabularyPracticePage';
import VocabularyNoteDetailPage from './components/VocabularyNoteDetailPage';
import VocabularyNoteEditorPage from './components/VocabularyNoteEditorPage';
import TriumphPage from './components/TriumphPage';
import PresetLibraryPage from './components/PresetLibraryPage';
import PresetDetailsPage from './components/PresetDetailsPage';
import StudyPathPage from './components/StudyPathPage';
import MarketPage from './components/MarketPage';
import MarketDetailsPage from './components/MarketDetailsPage';
import MarketPublishPage from './components/MarketPublishPage';
import MarketPublishFromNotePage from './components/MarketPublishFromNotePage';
import NoteEditPage from './components/NoteEditPage';
import NoteDetailHeader from './components/NoteDetailHeader';
import NoteCardList from './components/NoteCardList';
import StartNotePracticeAction from './components/StartNotePracticeAction';
import { normalizeNote } from './utils/notePresentation';
import {
  getNoteThemeStyle,
  normalizeNoteTheme,
} from './utils/noteTheme';
import NoteStatsPanel from './components/NoteStatsPanel';
import CoachingTimeOverlay from './components/CoachingTimeOverlay';
import NoCardsPracticeScene from './components/NoCardsPracticeScene';
import CelebrationPopup from './components/CelebrationPopup';
import { useConfirmDialog } from './components/ConfirmDialog';
import { NoteComposerPage } from './features/import-note';
import apiClient from './services/apiClient';
import authService from './services/AuthService';
import PracticeSettingsService from './services/PracticeSettingsService';
import StreakService from './services/StreakService';
import { getEndPracticeImage } from './services/TrainerAssets';
import { getPlayerLevel, isFirstSessionOfDay } from './utils/celebrationHelpers';
import { getNewlyUnlockedRecordAchievementIds } from './utils/recordAchievements';
import { getLanguage, t } from './i18n';
import playerLevelUpVideoWebm from './assets/videos/level-up-ceremony.webm';
import playerLevelUpVideoMp4 from './assets/videos/level-up-ceremony.mp4';
import playerLevelUpVideoPoster from './assets/videos/level-up-ceremony-poster.webp';
import { RECORD_ACHIEVEMENT_VISUALS } from './services/RecordCelebrationVisuals';
import { getCardLevelUpAnimation } from './services/LevelUpCelebrationAssets';
import { noteCompletionSnapshot, noteCompletionCelebrations } from './services/NoteCompletionCelebrations';
import usePageThemeColor from './hooks/usePageThemeColor';
import useHorizontalPageScrollLock from './hooks/useHorizontalPageScrollLock';
import './App.css';

/**
 * AppHeader component - localized, theme-aware brand
 * Hidden on practice page, map page, and login page
 */
function AppHeader() {
  const location = useLocation();

  if (showsAppNavigation(location.pathname)) return <AppNavigation />;
  if (['/signup', '/forgot-password', '/reset-password', '/verify-email'].includes(location.pathname)) return null;
  if (location.pathname.startsWith('/admin/') || location.pathname === '/level-up/practice' || location.pathname.startsWith('/level-up/result/') || location.pathname === '/vocabulary/practice') return null;

  // Hide the shared brand header on pages that provide their own page-level navigation.
  if (location.pathname === '/practice' || location.pathname === '/practice-player' || location.pathname === '/map' || location.pathname === '/import' || location.pathname.startsWith('/notes/') || location.pathname.startsWith('/library') || location.pathname.startsWith('/market') || location.pathname === '/calendar' || location.pathname === '/challenge' || location.pathname === '/tournament' || location.pathname === '/level-up' || location.pathname === '/triumph' || location.pathname === '/login' || location.pathname === '/admin') {
    return null;
  }

  return (
    <header className="App-header">
      <div className="app-title-logo">
        <AppLogo />
        <div className="app-title-subtitle">{t('app.title.subtitle')}</div>
      </div>
    </header>
  );
}

function AppMain({ children }) {
  const location = useLocation();
  const mainRef = useRef(null);
  const lockHorizontalPage = showsAppNavigationLinks(location.pathname);
  useHorizontalPageScrollLock(mainRef, lockHorizontalPage, location.pathname);
  const isPracticePlayer = location.pathname === '/practice-player';
  const isHome = location.pathname === '/' || location.pathname === '/main';
  const isNoteDetail = (location.pathname === '/map' && Boolean(location.state?.noteId))
    || /^\/library\/[^/]+\/?$/.test(location.pathname)
    || (/^\/market\/[^/]+\/?$/.test(location.pathname) && location.pathname !== '/market/publish');

  return (
    <>
      <main ref={mainRef} id="app-main-content" tabIndex={-1} className={`App-main${lockHorizontalPage ? ' App-main-horizontal-lock' : ''}${isPracticePlayer ? ' App-main-practice-player' : ''}${showsAppNavigation(location.pathname) ? ' App-main-utility' : ''}${isHome ? ' App-main-home' : ''}${isNoteDetail ? ' App-main-note-detail' : ''}`}>
        {children}
      </main>
    </>
  );
}

function TournamentRoute() {
  const [hasSeenEntryScene, setHasSeenEntryScene] = useState(false);

  if (!hasSeenEntryScene) {
    return <TournamentEntryScene onContinue={() => setHasSeenEntryScene(true)} />;
  }

  return <TournamentPage />;
}

/**
 * Main App component with routing
 * Requirements: 3.3 - Set up React Router with routes for map, practice, create/edit
 * Requirements: 1.2, 7.3 - Cache flash cards for routes that require a global pool
 */
function AppContent() {
  const location = useLocation();
  const [language, setAppLanguage] = useState(getLanguage);
  const [flashCards, setFlashCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const refreshLanguage = () => setAppLanguage(getLanguage());
    window.addEventListener('appLanguageChanged', refreshLanguage);
    return () => window.removeEventListener('appLanguageChanged', refreshLanguage);
  }, []);

  const hasSuppliedPracticeCards = Array.isArray(location.state?.testContext?.flashcards);
  const hasSuppliedPlayerCards = Array.isArray(location.state?.playerContext?.flashcards);
  const needsGlobalFlashCards = (
    (location.pathname === '/map' && !location.state?.noteId) ||
    location.pathname.startsWith('/edit/') ||
    (location.pathname === '/practice' && !hasSuppliedPracticeCards) ||
    (location.pathname === '/practice-player' && !hasSuppliedPlayerCards)
  );

  /**
   * Fetch all flash cards only for routes that have no scoped/navigation pool.
   * Requirements: 1.2, 7.3
   */
  const fetchFlashCards = useCallback(async () => {
    if (!authService.isLoggedIn()) {
      setFlashCards([]);
      setLoading(false);
      setError(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const cards = await apiClient.getAllFlashCards();
      setFlashCards(cards);
    } catch (err) {
      console.error('Failed to fetch flash cards:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!needsGlobalFlashCards) {
      setLoading(false);
      setError(null);
      return;
    }

    fetchFlashCards();
  }, [fetchFlashCards, needsGlobalFlashCards]);

  useEffect(() => {
    const handleAuthStateChanged = () => {
      setFlashCards([]);
      if (needsGlobalFlashCards) {
        fetchFlashCards();
      } else {
        setLoading(false);
        setError(null);
      }
    };

    window.addEventListener('authStateChanged', handleAuthStateChanged);
    return () => {
      window.removeEventListener('authStateChanged', handleAuthStateChanged);
    };
  }, [fetchFlashCards, needsGlobalFlashCards]);

  /**
   * Refresh flash cards after create/update/delete operations
   * Requirements: 1.1, 1.3, 1.4
   */
  const refreshFlashCards = () => {
    if (needsGlobalFlashCards) {
      fetchFlashCards();
    }
  };

  return (
    <div className={`App${showsAppNavigation(location.pathname) && !showsAppNavigationLinks(location.pathname) ? ' App-without-main-navigation' : ''}`} lang={language}>
      <AppHeader />
      <AppMain>
          {needsGlobalFlashCards && loading && <Spinner size="large" message={t('interface.common.cardsLoading')} />}
          {needsGlobalFlashCards && error && (
            <ErrorMessage 
              error={error}
              onRetry={fetchFlashCards}
              onDismiss={() => setError(null)}
              type={error?.isNetworkError ? 'network' : 'api'}
            />
          )}
          {(!needsGlobalFlashCards || (!loading && !error)) && (
            <Routes>
              {/* Login route - public */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<LoginPage />} />
              <Route path="/forgot-password" element={<LoginPage />} />
              <Route path="/reset-password" element={<LoginPage />} />
              <Route path="/verify-email" element={<LoginPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/admin/scenes" element={<AdminSceneBrowser />} />
              <Route path="/admin/presets" element={<AdminPresetBrowser />} />
              
              {/* Main page route - default */}
              <Route 
                path="/" 
                element={
                  <ProtectedRoute>
                    <MainPage key="home" userId={authService.getCurrentUserId()} />
                  </ProtectedRoute>
                } 
              />
              
              <Route
                path="/notes"
                element={
                  <ProtectedRoute>
                    <MainPage key="notes" view="notes" userId={authService.getCurrentUserId()} />
                  </ProtectedRoute>
                }
              />

              {/* Calendar page route */}
              <Route 
                path="/calendar" 
                element={
                  <ProtectedRoute>
                    <CalendarPage />
                  </ProtectedRoute>
                } 
              />

              <Route
                path="/user"
                element={
                  <ProtectedRoute>
                    <UserPage />
                  </ProtectedRoute>
                }
              />

              {/* Tournament page route */}
              <Route
                path="/tournament"
                element={
                  <ProtectedRoute>
                    <TournamentRoute />
                  </ProtectedRoute>
                }
              />

              {/* Legacy challenge route */}
              <Route
                path="/challenge"
                element={
                  <ProtectedRoute>
                    <TournamentRoute />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/level-up"
                element={
                  <ProtectedRoute>
                    <CardLevelUpgradePage />
                  </ProtectedRoute>
                }
              />
              <Route path="/level-up/practice" element={<ProtectedRoute><WritingPracticePage /></ProtectedRoute>} />
              <Route path="/level-up/result/:examId" element={<ProtectedRoute><WritingResultPage /></ProtectedRoute>} />
              <Route path="/vocabulary" element={<ProtectedRoute><VocabularyPage /></ProtectedRoute>} />
              <Route path="/vocabulary/practice" element={<ProtectedRoute><VocabularyPracticePage /></ProtectedRoute>} />
              <Route path="/vocabulary/new" element={<ProtectedRoute><VocabularyNoteEditorPage /></ProtectedRoute>} />
              <Route path="/vocabulary/notes/:noteId" element={<ProtectedRoute><VocabularyNoteDetailPage /></ProtectedRoute>} />
              <Route path="/vocabulary/notes/:noteId/edit" element={<ProtectedRoute><VocabularyNoteEditorPage /></ProtectedRoute>} />

              <Route
                path="/triumph"
                element={
                  <ProtectedRoute>
                    <TriumphPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/library"
                element={
                  <ProtectedRoute>
                    <PresetLibraryPage />
                  </ProtectedRoute>
                }
              />

              <Route path="/study-path" element={<ProtectedRoute><StudyPathPage /></ProtectedRoute>} />

              <Route
                path="/library/:presetId"
                element={
                  <ProtectedRoute>
                    <PresetDetailsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/market"
                element={<ProtectedRoute><MarketPage /></ProtectedRoute>}
              />
              <Route
                path="/market/:listingId"
                element={<ProtectedRoute><MarketDetailsPage /></ProtectedRoute>}
              />
              <Route
                path="/market/publish"
                element={<ProtectedRoute><MarketPublishPage /></ProtectedRoute>}
              />
              
              {/* Legacy routes - kept for backward compatibility */}
              <Route 
                path="/main" 
                element={
                  <ProtectedRoute>
                    <MainPage key="home" userId={authService.getCurrentUserId()} />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/map" 
                element={
                  <MapView 
                    flashCards={flashCards} 
                    onRefresh={refreshFlashCards}
                    onFlashCardsUpdate={setFlashCards}
                  />
                } 
              />
              <Route 
                path="/create" 
                element={
                  <CreateView 
                    onRefresh={refreshFlashCards}
                  />
                } 
              />
              <Route 
                path="/edit/:id" 
                element={
                  <EditView 
                    flashCards={flashCards}
                    onRefresh={refreshFlashCards}
                  />
                } 
              />
              <Route 
                path="/import" 
                element={
                  <NoteComposerPage
                    onRefresh={refreshFlashCards}
                  />
                } 
              />
              <Route
                path="/notes/:noteId/add-cards"
                element={
                  <NoteComposerPage
                    onRefresh={refreshFlashCards}
                  />
                }
              />
              <Route
                path="/practice"
                element={
                  <PracticeView
                    flashCards={flashCards}
                    onFlashCardsUpdate={setFlashCards}
                  />
                }
              />
              <Route path="/notes/:noteId/edit" element={<ProtectedRoute><NoteEditPage onRefresh={refreshFlashCards} /></ProtectedRoute>} />
              <Route path="/notes/:noteId/publish" element={<ProtectedRoute><MarketPublishFromNotePage /></ProtectedRoute>} />
              <Route
                path="/practice-player"
                element={
                  <ProtectedRoute>
                    <PracticePlayerPage
                      flashCards={flashCards}
                      onFlashCardsUpdate={setFlashCards}
                    />
                  </ProtectedRoute>
                }
              />
            </Routes>
          )}
      </AppMain>
    </div>
  );
}

function App() {
  const [router, setRouter] = useState(null);
  useEffect(() => {
    const appRouter = createBrowserRouter([
      { path: '*', element: <NavigationGuardProvider><AppContent /></NavigationGuardProvider> },
    ], { future: { v7_relativeSplatPath: true } });
    setRouter(appRouter);
    return () => appRouter.dispose();
  }, []);
  return router ? <RouterProvider router={router} future={{ v7_startTransition: true }} /> : null;
}

/**
 * Map View - Display and search all flash cards
 * Can filter by noteId if passed through navigation state
 * Requirements: 3.1, 3.3
 */
function MapView({ flashCards, onRefresh, onFlashCardsUpdate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [loadedNoteId, setLoadedNoteId] = useState(null);
  const [noteLoadError, setNoteLoadError] = useState('');
  const [noteLoadAttempt, setNoteLoadAttempt] = useState(0);
  const [noteFlashcards, setNoteFlashcards] = useState([]);
  const [noteName, setNoteName] = useState('');
  const [editingCard, setEditingCard] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isLevelUpDialogOpen, setIsLevelUpDialogOpen] = useState(false);
  const [reviewSource, setReviewSource] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [coverUrl, setCoverUrl] = useState(null);
  const [noteMetadata, setNoteMetadata] = useState(null);
  const [isNoteStateSaving, setIsNoteStateSaving] = useState(false);
  const [levelUpCelebration, setLevelUpCelebration] = useState(null);
  const { confirm, alert: showAlert, confirmDialog } = useConfirmDialog();
  // Get noteId from navigation state
  const noteId = location.state?.noteId;
  const isNoteDisabled = Boolean(noteId && noteMetadata?.isEnabled === false);
  const pageTheme = useMemo(() => normalizeNoteTheme(isNoteDisabled
    ? { themeColorLight: '#e5e5e5', themeColorDark: '#292929' }
    : noteMetadata || {}), [isNoteDisabled, noteMetadata]);
  usePageThemeColor(pageTheme);

  // Fetch only this note's cards and metadata. Avoid loading every card merely
  // to derive the note title or cover.
  useEffect(() => {
    let cancelled = false;
    setNoteLoadError('');
    setLoadedNoteId(null);
    setNoteFlashcards([]);
    setNoteName('');
    setCoverUrl(null);
    setNoteMetadata(null);
    setLoading(Boolean(noteId));
    if (!noteId) return undefined;

    const fetchNoteFlashcards = async () => {
      try {
        const databaseService = (await import('./services/DatabaseService')).default;
        if (cancelled) return;
        const [cards, note] = await Promise.all([
          databaseService.getFlashcardsByNote(noteId),
          apiClient.getNoteMetadata(noteId),
        ]);
        if (cancelled) return;
        if (!note || !Array.isArray(cards)) throw new Error(t('notesUnified.notFound'));
        setNoteFlashcards(cards);
        setNoteName(note.noteTitle || note.title || note.name || `Note ${noteId}`);
        setCoverUrl(note.coverUrl || null);
        setNoteMetadata(note);
        setLoadedNoteId(noteId);
      } catch (err) {
        if (cancelled) return;
        setNoteLoadError(err.message || t('interface.common.unexpected'));
        setLoadedNoteId(noteId);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchNoteFlashcards();
    return () => { cancelled = true; };
  }, [noteId, noteLoadAttempt]);

  useEffect(() => {
    if (!isEditDialogOpen) return undefined;

    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalBodyWidth = document.body.style.width;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.width = originalBodyWidth;
      try {
        if (window.navigator?.userAgent?.includes('jsdom')) return;
        window.scrollTo(0, scrollY);
      } catch {
        // Browser-only scroll restoration.
      }
    };
  }, [isEditDialogOpen]);

  const handleCardEdit = (card) => {
    setEditingCard(card);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingCard(null);
  };

  const handleNoteStateToggle = async () => {
    if (!noteId || !noteMetadata || isNoteStateSaving) return;
    const nextIsEnabled = noteMetadata.isEnabled === false;
    setIsNoteStateSaving(true);
    try {
      const updated = await apiClient.setNoteEnabled(noteId, nextIsEnabled);
      setNoteMetadata((current) => ({
        ...current,
        ...updated,
        isEnabled: updated?.isEnabled !== false,
      }));
    } catch (error) {
      await showAlert({
        title: t('notes.stateUpdateFailedTitle'),
        message: t('notes.stateUpdateFailedMessage'),
        confirmLabel: t('common.ok'),
        tone: 'danger',
      });
    } finally {
      setIsNoteStateSaving(false);
    }
  };

  const handleCloseLevelUpDialog = () => {
    setIsLevelUpDialogOpen(false);
    setEditingCard(null);
  };

  const handleEditSubmit = async (formData) => {
    try {
      const updatedCard = await apiClient.updateFlashCard(editingCard.id, formData);
      setNoteFlashcards(prev => prev.map(c => c.id === updatedCard.id ? { ...c, ...updatedCard } : c));
      if (onFlashCardsUpdate) {
        onFlashCardsUpdate(prev => prev.map(c => c.id === updatedCard.id ? { ...c, ...updatedCard } : c));
      }
      onRefresh();
      handleCloseEditDialog();
    } catch (err) {
      throw err; // Let the form handle the error
    }
  };

  const handleEditLevelUp = async (formData) => {
    try {
      const result = await apiClient.levelUpFlashCard(editingCard.id, formData);
      const sourceCard = result.sourceCard;
      const createdCard = result.createdCard;
      setNoteFlashcards(prev => prev.map(c => c.id === sourceCard.id ? { ...c, ...sourceCard } : c));
      if (onFlashCardsUpdate) {
        onFlashCardsUpdate(prev => [...prev.map(c => c.id === sourceCard.id ? { ...c, ...sourceCard } : c), createdCard]);
      }
      onRefresh();
      handleCloseEditDialog();
      setIsLevelUpDialogOpen(false);
      setLevelUpCelebration({
        level: PracticeSettingsService.getCardLevel(createdCard),
      });
    } catch (err) {
      throw err;
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete Flash Card?',
      message: 'This card will be removed from the drill.',
      confirmLabel: 'Delete',
      cancelLabel: 'Keep',
      tone: 'danger',
    });

    if (!confirmed) return;

    try {
      await apiClient.deleteFlashCard(editingCard.id);
      setNoteFlashcards(prev => prev.filter(c => c.id !== editingCard.id));
      if (onFlashCardsUpdate) {
        onFlashCardsUpdate(prev => prev.filter(c => c.id !== editingCard.id));
      }
      onRefresh();
      handleCloseEditDialog();
    } catch (err) {
      console.error('Failed to delete flash card:', err);
      await showAlert({
        title: 'Delete Failed',
        message: 'Failed to delete flash card. Please try again.',
        confirmLabel: t('common.ok'),
        tone: 'danger',
      });
    }
  };

  // Use note-specific flashcards if noteId is provided, otherwise use all flashcards
  const displayFlashcards = noteId ? noteFlashcards : flashCards;

  const handleBackToNotes = () => {
    navigate('/notes');
  };

  const handleDeleteNote = async () => {
    if (!noteId) return;
    
    const confirmMessage = t('interface.map.deleteConfirmMessage', undefined, {
      name: noteName, count: noteFlashcards.length,
    });
    
    const confirmed = await confirm({
      title: t('interface.map.deleteConfirmTitle'),
      message: confirmMessage,
      confirmLabel: t('interface.map.delete'),
      cancelLabel: t('common.cancel'),
      tone: 'danger',
    });

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      // Import DatabaseService to delete note
      const databaseService = (await import('./services/DatabaseService')).default;
      await databaseService.deleteNote(noteId);

      // Return to the collection after deleting a note, not the training dashboard.
      onRefresh();
      navigate('/notes');
    } catch (err) {
      console.error('Failed to delete note:', err);
      await showAlert({
        title: t('interface.map.deleteFailed'),
        message: err.message || t('interface.common.unexpected'),
        confirmLabel: t('common.ok'),
        tone: 'danger',
      });
      setIsDeleting(false);
    }
  };

  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyToClipboard = () => {
    const text = displayFlashcards
      .map(card => {
        const cardType = PracticeSettingsService.getCardType(card);
        if (cardType === PracticeSettingsService.CARD_TYPES.READING) {
          const readingBody = Array.isArray(card.readingSegments) && card.readingSegments.length > 0
            ? [
              card.readingCharacterNames?.A && card.readingCharacterNames.A !== 'A'
                ? `@A name: ${card.readingCharacterNames.A}`
                : null,
              card.readingCharacterNames?.B && card.readingCharacterNames.B !== 'B'
                ? `@B name: ${card.readingCharacterNames.B}`
                : null,
              ...card.readingSegments.map((segment) => {
                if (segment.type === 'scene') {
                  return `@Scene: ${segment.text || ''}`;
                }
                if (segment.type === 'story') {
                  return `@Story: ${segment.text || ''}`;
                }
                return `@${segment.speaker === 'B' ? 'B' : 'A'}${segment.practiceMode === 'once' ? '[once]' : ''}: ${segment.text || ''}`;
              }),
            ].filter(Boolean).join('\n')
            : `@Story: ${card.readingContent || card.back || ''}`;
          return `@R: ${card.readingTitle || card.front}\n${readingBody}`;
        }
        const needsTaggedExport = Boolean(
          card.contextPre ||
          card.memo ||
          card.contextAfter ||
          card.backMemo ||
          card.ttsText ||
          cardType === PracticeSettingsService.CARD_TYPES.SIMPLE
        );
        if (needsTaggedExport) {
          return [
            card.contextPre ? `@pre: ${card.contextPre}` : null,
            cardType === PracticeSettingsService.CARD_TYPES.SIMPLE ? '@word: true' : null,
            `@Q: ${card.front}`,
            card.memo ? `@front: ${card.memo}` : null,
            `@A: ${card.back}`,
            card.ttsText ? `@tts: ${card.ttsText}` : null,
            card.backMemo ? `@back: ${card.backMemo}` : null,
            card.contextAfter ? `@after: ${card.contextAfter}` : null,
          ].filter(Boolean).join('\n');
        }
        return `${card.front}\n${card.back}`;
      })
      .join('\n---\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Set page title - show loading state if noteId exists but noteName hasn't loaded yet
  let pageTitle = t('interface.map.title');
  if (noteId) {
    pageTitle = noteName ? t('interface.map.noteTitle', undefined, { name: noteName }) : t('interface.common.loading');
  }

  if (noteId && (loading || loadedNoteId !== noteId)) {
    return (
      <div className="map-view">
        <Spinner size="medium" message={t('interface.common.cardsLoading')} />
      </div>
    );
  }

  if (noteId && noteLoadError) {
    return <div className="map-view" role="alert">
      <p className="note-detail-error">{t('noteComposer.errors.loadNote', undefined, { details: noteLoadError })}</p>
      <div className="note-detail-cta">
        <button type="button" className="note-detail-button is-primary" onClick={() => setNoteLoadAttempt((attempt) => attempt + 1)}>{t('interface.common.retry')}</button>
        <button type="button" className="note-detail-button" onClick={handleBackToNotes}>{t('interface.navigation.backToNotes')}</button>
      </div>
    </div>;
  }

  const displayedNote = noteId
    ? normalizeNote({ ...noteMetadata, noteId, noteTitle: noteName || t('interface.common.loading'), cardCount: displayFlashcards.length }, 'personal')
    : null;
  const noteThemeStyle = getNoteThemeStyle(isNoteDisabled ? pageTheme : displayedNote);

  return (
    <div
      className={`map-view${noteId ? ' note-detail-page' : ''}${noteThemeStyle ? ' has-note-theme' : ''}${isNoteDisabled ? ' is-note-disabled' : ''}`}
      style={noteThemeStyle}
    >
      {isDeleting && (
        <div className="deleting-overlay">
          <Spinner size="large" message={t('interface.map.deleting')} />
        </div>
      )}
      {noteId ? (
        <NoteDetailHeader
          note={displayedNote}
          backLabel={t('interface.navigation.notes')} onBack={handleBackToNotes}
          cover={coverUrl
            ? <img src={coverUrl} alt={t('interface.map.cover')} />
            : <span className="map-cover-placeholder" role="img" aria-label={t('interface.map.cover')}><ImagePlus size={44} strokeWidth={1.6} aria-hidden="true" /></span>}
          actions={<MapNoteActions
            onReview={displayFlashcards.length > 0 && displayFlashcards.every(PracticeSettingsService.isGraduated) ? () => setReviewSource({ note: { ...noteMetadata, noteId, noteTitle: noteName } }) : undefined}
            onEdit={() => navigate(`/notes/${encodeURIComponent(noteId)}/edit`)}
            onAdd={() => navigate(`/notes/${encodeURIComponent(noteId)}/add-cards`)}
            onCopy={handleCopyToClipboard} onDelete={handleDeleteNote}
            onPublish={(!noteMetadata?.sourcePresetId && !noteMetadata?.sourceMarketListingId)
              ? () => navigate(`/notes/${encodeURIComponent(noteId)}/publish`) : undefined}
            onToggleState={handleNoteStateToggle} copySuccess={copySuccess}
            noteEnabled={noteMetadata?.isEnabled !== false} stateSaving={isNoteStateSaving} disabled={isDeleting}
          />}
          primaryAction={<StartNotePracticeAction noteId={noteId} note={displayedNote} flashcards={displayFlashcards} disabled={isDeleting || !displayFlashcards.length} />}
        />
      ) : (
        <div className="map-page-header">
          <h1 className="page-title-fx page-title-map">{pageTitle}</h1>
        </div>
      )}
      {noteId && (
        <NoteStatsPanel
          flashCards={displayFlashcards}
          noteName={noteName}
          noteId={noteId}
        />
      )}
      {noteId ? <NoteCardList key={noteId} cards={displayFlashcards} personal onAdd={() => navigate(`/notes/${encodeURIComponent(noteId)}/add-cards`)} onCardEdit={handleCardEdit} onCardReview={(card) => setReviewSource({ card })} /> : <FlashCardMap
        flashCards={displayFlashcards} onCardSelect={() => {}} onCardEdit={handleCardEdit} onCardReview={(card) => setReviewSource({ card })}
      />}
      
      {reviewSource && <ReviewLevelUpDialog {...reviewSource} onClose={() => setReviewSource(null)} onCreated={() => onRefresh()} />}
      {/* Edit Dialog */}
      {isEditDialogOpen && editingCard && (
        <div className="modal-overlay" onClick={handleCloseEditDialog}>
          <div className="modal-content flashcard-edit-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('interface.map.editCard')}</h2>
              <button type="button" className="modal-close" onClick={handleCloseEditDialog} aria-label={t('interface.common.close')}>{'\u00D7'}</button>
            </div>
            <FlashCardForm
              initialData={editingCard}
              onSubmit={handleEditSubmit}
              onCancel={handleCloseEditDialog}
              onDelete={handleDelete}
              showCancelAction={false}
            />
          </div>
        </div>
      )}

      {isLevelUpDialogOpen && editingCard && (
        <div className="modal-overlay" onClick={handleCloseLevelUpDialog}>
          <div className="modal-content flashcard-edit-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('interface.map.levelUp')}</h2>
              <button type="button" className="modal-close" onClick={handleCloseLevelUpDialog} aria-label={t('interface.common.close')}>{'\u00D7'}</button>
            </div>
            <LevelUpFlashCardForm
              initialData={editingCard}
              onLevelUp={handleEditLevelUp}
              onCancel={handleCloseLevelUpDialog}
            />
          </div>
        </div>
      )}


      <CelebrationPopup
        isVisible={Boolean(levelUpCelebration)}
        onClose={() => setLevelUpCelebration(null)}
        title="Level Up!"
        message={levelUpCelebration ? `Card reached Level ${levelUpCelebration.level}. Keep building your own sentence.` : ''}
        {...getCardLevelUpAnimation(levelUpCelebration?.level)}
      />
      {confirmDialog}
    </div>
  );
}

/**
 * Create View - Form for creating new flash cards
 * Requirements: 1.1
 */
function CreateView({ onRefresh }) {
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (formData) => {
    try {
      await apiClient.createFlashCard(formData);
      setSuccess(true);
      onRefresh();
      // Navigate back to map after a short delay
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      throw err; // Let the form handle the error
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  return (
    <div className="create-view">
      {success && (
        <div className="success-message">
          Flash card created successfully! Redirecting...
        </div>
      )}
      <FlashCardForm 
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </div>
  );
}

/**
 * Edit View - Form for editing existing flash cards
 * Requirements: 1.3
 */
function EditView({ flashCards, onRefresh }) {
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [cardToEdit, setCardToEdit] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLevelUpFormOpen, setIsLevelUpFormOpen] = useState(false);
  const [levelUpCelebration, setLevelUpCelebration] = useState(null);
  const { confirm, alert: showAlert, confirmDialog } = useConfirmDialog();
  
  // Get card ID from URL
  const cardId = window.location.pathname.split('/').pop();

  useEffect(() => {
    // Find the card to edit
    const card = flashCards.find(c => c.id === cardId);
    setCardToEdit(card);
  }, [cardId, flashCards]);

  const handleSubmit = async (formData) => {
    try {
      await apiClient.updateFlashCard(cardId, formData);
      setSuccess(true);
      onRefresh();
      // Navigate back to map after a short delay
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      throw err; // Let the form handle the error
    }
  };

  const handleLevelUp = async (formData) => {
    try {
      const result = await apiClient.levelUpFlashCard(cardId, formData);
      setCardToEdit(result.sourceCard);
      setIsLevelUpFormOpen(false);
      setSuccess(false);
      onRefresh();
      setLevelUpCelebration({
        level: PracticeSettingsService.getCardLevel(result.createdCard),
      });
    } catch (err) {
      throw err;
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete Flash Card?',
      message: 'This card will be removed from the drill.',
      confirmLabel: 'Delete',
      cancelLabel: 'Keep',
      tone: 'danger',
    });

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await apiClient.deleteFlashCard(cardId);
      onRefresh();
      navigate('/');
    } catch (err) {
      await showAlert({
        title: 'Delete Failed',
        message: `Failed to delete flash card: ${err.message}`,
        confirmLabel: 'OK',
        tone: 'danger',
      });
      setIsDeleting(false);
    }
  };

  if (!cardToEdit) {
    return (
      <div className="edit-view">
        <p>Flash card not found.</p>
        <button onClick={() => navigate('/')}>Back to Map</button>
      </div>
    );
  }

  return (
    <div className="edit-view">
      {success && (
        <div className="success-message">
          Flash card updated successfully! Redirecting...
        </div>
      )}
      {isDeleting && (
        <div className="deleting-overlay">
          <Spinner size="large" message="Deleting flash card..." />
        </div>
      )}
      {isLevelUpFormOpen ? (
        <LevelUpFlashCardForm
          initialData={cardToEdit}
          onLevelUp={handleLevelUp}
          onCancel={() => setIsLevelUpFormOpen(false)}
        />
      ) : (
        <>
          <FlashCardForm
            initialData={cardToEdit}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
          <div className="edit-actions">
            {PracticeSettingsService.isCardUpgradeable(cardToEdit) && (
              <button
                type="button"
                className="btn btn-level-up-form"
                onClick={() => navigate('/level-up')}
              >
                {t('writingExam.title')}
              </button>
            )}
            <button
              onClick={handleDelete}
              className="btn-delete"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Flash Card'}
            </button>
          </div>
        </>
      )}
      <CelebrationPopup
        isVisible={Boolean(levelUpCelebration)}
        onClose={() => {
          setLevelUpCelebration(null);
          navigate('/');
        }}
        title="Level Up!"
        message={levelUpCelebration ? `Card reached Level ${levelUpCelebration.level}. Keep building your own sentence.` : ''}
        {...getCardLevelUpAnimation(levelUpCelebration?.level)}
      />
      {confirmDialog}
    </div>
  );
}

/**
 * Practice View - Practice session with flash cards
 * Requirements: 6.1, 6.5
 */
function PracticeView({ flashCards, onFlashCardsUpdate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirm, alert: showAlert, confirmDialog } = useConfirmDialog();
  const [sessionComplete, setSessionComplete] = useState(false);
  const [sessionResults, setSessionResults] = useState(null);
  const [resultCelebrations, setResultCelebrations] = useState([]);
  const [practiceAccess, setPracticeAccess] = useState(null);
  const practiceStartPromiseRef = useRef(null);
  const coachingSelectionPromiseRef = useRef(null);
  const tournamentIntroPromiseRef = useRef(null);
  const startingProfilePromiseRef = useRef(null);
  const startingNotesPromiseRef = useRef(null);
  const sessionCompletionHandledRef = useRef(false);
  const [practiceGateState, setPracticeGateState] = useState({
    status: 'checking',
    coachingMessage: null,
    opponentIntro: null,
  });

  // Get flashcards and settings from navigation state if available (for note-specific practice)
  // Otherwise use all flashcards from App state
  const testContext = location.state?.testContext;
  const isQuickPractice = testContext?.settings?.practiceMode === 'quick';
  const quickReturnTo = isQuickPractice && (/^\/(library|market)\//.test(testContext?.returnTo || '') || testContext?.returnTo === '/map') ? testContext.returnTo : '/library';
  const practiceFlashCards = useMemo(() => {
    if (isQuickPractice) return testContext?.flashcards || [];
    if (!testContext?.flashcards) return flashCards;

    const latestCardsById = new Map((flashCards || []).map((card) => [card.id, card]));
    return testContext.flashcards.map((card) => {
      const latestCard = latestCardsById.get(card.id);
      const mergedCard = latestCard ? { ...card, ...latestCard } : card;
      const noteId = testContext.noteId ?? testContext.note?.noteId;
      const noteDifficulty = testContext.note?.difficulty;
      // Note-detail cards do not necessarily include their parent difficulty.
      // Preserve that metadata when preparing the practice queue.
      return noteDifficulty != null && noteId != null && (
        mergedCard.noteId == null || String(mergedCard.noteId) === String(noteId)
      ) ? { ...mergedCard, noteDifficulty } : mergedCard;
    });
  }, [flashCards, testContext, isQuickPractice]);
  const practiceSettings = useMemo(() => testContext?.settings || {}, [testContext?.settings]);
  const isCompletedReviewPractice = practiceSettings.practiceMode === 'completedReview' || testContext?.practiceMode === 'completedReview';
  const availablePracticeCardCount = useMemo(() => (
    isCompletedReviewPractice
      ? practiceFlashCards.length
      : PracticeSettingsService.applyFilters(practiceFlashCards, practiceSettings).length
  ), [isCompletedReviewPractice, practiceFlashCards, practiceSettings]);

  useEffect(() => {
    if (
      sessionComplete ||
      practiceFlashCards.length === 0 ||
      practiceGateState.status !== 'ready' ||
      typeof window === 'undefined'
    ) {
      return undefined;
    }

    const guardId = `practice-navigation-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const originalState = window.history.state;
    const originalUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const guard = {
      guardId,
      promptOpen: false,
      restoring: false,
    };

    window.history.pushState(
      { ...(originalState && typeof originalState === 'object' ? originalState : {}), __practiceNavigationGuard: guardId },
      '',
      originalUrl,
    );

    const handlePopState = () => {
      if (guard.restoring) {
        guard.restoring = false;
        return;
      }

      if (guard.promptOpen) {
        window.history.forward();
        return;
      }

      guard.promptOpen = true;
      window.history.forward();
      void showAlert({
        title: t('practiceUi.navigation.title'),
        message: t('practiceUi.navigation.message'),
        confirmLabel: 'OK',
        tone: 'warning',
      }).finally(() => {
        guard.promptOpen = false;
      });
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.__practiceNavigationGuard === guardId) {
        window.history.replaceState(originalState, '', originalUrl);
      }
    };
  }, [practiceFlashCards.length, practiceGateState.status, sessionComplete, showAlert]);

  useEffect(() => {
    let cancelled = false;

    if (sessionComplete || practiceFlashCards.length === 0) {
      setPracticeGateState({ status: 'ready', coachingMessage: null, opponentIntro: null });
      return () => {
        cancelled = true;
      };
    }

    // Card progress changes practice eligibility during an active session. The entry
    // gate must only react to those counts while it is still opening the session.
    if (practiceGateState.status !== 'checking') {
      return () => {
        cancelled = true;
      };
    }

    if (availablePracticeCardCount === 0) {
      setPracticeGateState({ status: 'empty', coachingMessage: null, opponentIntro: null });
      return () => {
        cancelled = true;
      };
    }

    setPracticeGateState({ status: 'checking', coachingMessage: null, opponentIntro: null });

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!practiceStartPromiseRef.current) {
      startingNotesPromiseRef.current = isQuickPractice || isCompletedReviewPractice
        ? Promise.resolve(null)
        : noteCompletionSnapshot();
      startingProfilePromiseRef.current = Promise.resolve(apiClient.getCurrentUserSummary())
        .catch((error) => {
          console.warn('Failed to load starting player profile:', error);
          return null;
        });
      practiceStartPromiseRef.current = isQuickPractice && testContext?.access ? Promise.resolve(testContext.access) : apiClient.startPractice({
        timezone,
        mode: testContext?.settings?.practiceMode || testContext?.practiceMode || testContext?.type || 'normal',
      });
    }

    if (!tournamentIntroPromiseRef.current) {
      tournamentIntroPromiseRef.current = Promise.resolve(practiceStartPromiseRef.current).then(() => (
        (!isQuickPractice && apiClient.getTournamentPracticeIntro?.()) || { shouldShow: false, intro: null }
      )).catch((error) => {
        console.warn('Failed to load tournament practice intro:', error);
        return { shouldShow: false, intro: null };
      });
    }
    const opponentPromise = tournamentIntroPromiseRef.current;
    const quickSceneTrigger = isQuickPractice && getQuickPracticeSceneTrigger(testContext?.settings?.quickSource);
    if ((!isQuickPractice || quickSceneTrigger) && !coachingSelectionPromiseRef.current) {
      const requestId = createSceneSelectionRequestId();
      coachingSelectionPromiseRef.current = Promise.all([practiceStartPromiseRef.current, opponentPromise]).then(async ([, opponentResult]) => {
        // Check first: selecting an ordinary Scene would consume its story history
        // even if a tournament contender's entry later hides that Scene.
        if (!isQuickPractice && (opponentResult?.skipCoaching || (opponentResult?.shouldShow && opponentResult.intro))) {
          return { shouldShow: false, message: null };
        }
        if (!quickSceneTrigger) return apiClient.getCoachingTime({ timezone, requestId });
        const result = await apiClient.getRandomScene({ trigger: quickSceneTrigger, timezone, requestId });
        return { ...result, shouldShow: Boolean(result?.scene?.frames?.some(frame => typeof frame?.text === 'string' && frame.text.trim())) };
      }).catch((error) => {
        console.warn('Failed to load coaching time:', error);
        return { shouldShow: false, message: null };
      });
    }
    const coachingPromise = isQuickPractice && !quickSceneTrigger
      ? Promise.resolve({ shouldShow: false, message: null })
      : coachingSelectionPromiseRef.current;
    Promise.all([
      practiceStartPromiseRef.current,
      coachingPromise,
      opponentPromise,
      // Capture the baseline before any card can be practiced (including checkpoints).
      startingNotesPromiseRef.current,
    ]).then(async ([startResult, coachingResult, opponentResult]) => {
      if (cancelled) return;
      setPracticeAccess(startResult);

      const nextState = {
        status: 'ready',
        coachingMessage: coachingResult?.shouldShow ? (coachingResult.scene || coachingResult.message) : null,
        opponentIntro: opponentResult?.shouldShow ? opponentResult.intro : null,
      };

      if (nextState.coachingMessage) {
        nextState.status = 'coaching';
      } else if (nextState.opponentIntro) {
        nextState.status = 'opponent';
      }

      setPracticeGateState(nextState);
    }).catch(async (error) => {
      if (cancelled) return;
      const code = error?.data?.error?.code;
      if (code === 'DAILY_PRACTICE_LIMIT_REACHED') {
        await showAlert({
          title: t('membership.dailyLimit.title'),
          message: t('membership.dailyLimit.message'),
          confirmLabel: t('common.ok'),
          tone: 'warning',
        });
        if (!cancelled) navigate('/');
        return;
      }
      console.warn('Failed to start practice:', error);
      await showAlert({
        title: t('membership.practiceUnavailable.title'),
        message: t('membership.practiceUnavailable.message'),
        confirmLabel: t('common.ok'),
        tone: 'warning',
      });
      if (!cancelled) navigate('/');
    });

    return () => {
      cancelled = true;
    };
  }, [availablePracticeCardCount, navigate, practiceFlashCards.length, practiceGateState.status, sessionComplete, showAlert, testContext, isQuickPractice, isCompletedReviewPractice]);

  const continueAfterCoaching = () => {
    setPracticeGateState((current) => {
      if (current.opponentIntro) {
        return { ...current, status: 'opponent' };
      }
      return { ...current, status: 'ready' };
    });
  };

  const continueAfterOpponentIntro = () => {
    setPracticeGateState((current) => ({ ...current, status: 'ready' }));
  };

  /**
   * Handle request for next batch of cards in infinity mode
   * Requirements: 2.4, 6.2
   * @param {Array} previousBatch - The previous batch of cards
   * @returns {Promise<Array>} Next batch of 30 cards
   */
  const handleRequestNextBatch = async (previousBatch) => {
    try {
      // Get the full flashcard pool from the practice context
      // practiceFlashCards contains the cards from navigation state (testContext)
      // or falls back to the App-level flashCards prop
      const fullFlashcardPool = practiceFlashCards;

      // Extract filter configuration from settings
      const filters = {
        maxExcludeGrade: practiceSettings.maxExcludeGrade,
        excludedGrades: practiceSettings.excludedGrades || [],
        excludeByPracticeCount: practiceSettings.excludeByPracticeCount || false,
        practiceCountThreshold: practiceSettings.practiceCountThreshold || 200,
        practiceCardTypeFilter: practiceSettings.practiceCardTypeFilter,
        maxScore: PracticeSettingsService.normalizeMaxScore(
          practiceSettings.maxScore ?? practiceSettings.maxRepeatCount
        ),
      };

      // Call PracticeSettingsService to select next batch
      const nextBatch = PracticeSettingsService.selectNextBatch(
        fullFlashcardPool,
        filters,
        previousBatch,
        30 // batch size
      );

      return nextBatch;
    } catch (error) {
      console.error('Failed to select next batch:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (!sessionComplete || !sessionResults) return undefined;
    let cancelled = false;

    const loadCompletedNotes = async () => {
      if (sessionResults.sessionSaved !== true || !sessionResults.practicedCards?.length) return [];
      const before = await startingNotesPromiseRef.current;
      if (!before || cancelled) return [];
      const after = await noteCompletionSnapshot();
      return cancelled ? [] : noteCompletionCelebrations(before, after, sessionResults.practicedCards);
    };

    const loadPostSessionCelebrations = async () => {
      if (sessionResults.sessionSaved !== true || !(Number(sessionResults.scoreGained) > 0)) {
        return { levelUp: null, achievements: [] };
      }

      const startingProfile = startingProfilePromiseRef.current
        ? await startingProfilePromiseRef.current
        : null;
      const previousLevel = getPlayerLevel(startingProfile?.summary);
      if (!previousLevel || cancelled) return { levelUp: null, achievements: [] };

      try {
        // Saving the session invalidates this API's summary cache, including after checkpoints.
        const profile = await apiClient.getCurrentUserSummary();
        if (cancelled) return { levelUp: null, achievements: [] };
        const level = getPlayerLevel(profile?.summary);
        const levelUp = level && level > previousLevel ? {
          id: `player-level:${level}`,
          kind: 'playerLevelUp',
          colorTheme: 'level-up',
          title: t('playerLevelUp.title'),
          message: t('playerLevelUp.message', undefined, { previousLevel, level }),
          animationVideoWebmSrc: playerLevelUpVideoWebm,
          animationVideoMp4Src: playerLevelUpVideoMp4,
          animationVideoPosterSrc: playerLevelUpVideoPoster,
          animationFrameSize: 240,
          animationDurationMs: 2000,
        } : null;
        const newlyUnlocked = getNewlyUnlockedRecordAchievementIds(startingProfile, profile);
        const achievements = newlyUnlocked.map((id) => {
          const visual = RECORD_ACHIEVEMENT_VISUALS[id];
          if (!visual) return null;
          const label = t(visual.labelKey);
          return {
            id: `record:${id}`,
            kind: 'recordAchievement',
            title: t('recordAchievement.title'),
            message: t('recordAchievement.message', undefined, { record: label }),
            iconSrc: visual.artwork,
            iconAlt: label,
            iconLayout: 'wide',
          };
        }).filter(Boolean);
        return { levelUp, achievements };
      } catch (error) {
        console.warn('Failed to load completed training records:', error);
        return { levelUp: null, achievements: [] };
      }
    };

    const loadCelebrations = async () => {
      const postSessionPromise = loadPostSessionCelebrations();
      const completedNotesPromise = loadCompletedNotes();
      const trainedSomething = Number(sessionResults.cardsStudied) > 0 || Number(sessionResults.repeatCount) > 0;
      if (trainedSomething && isFirstSessionOfDay(sessionResults)) {
        let streak = 0;
        try {
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const streakInfo = await new StreakService().getStreakData(timezone);
          streak = Number(streakInfo?.streak) || 0;
        } catch (error) {
          console.warn('Failed to load streak data for celebration:', error);
        }
        if (cancelled) return;
        const isNewStart = streak <= 1;
        setResultCelebrations([{
          id: 'daily-streak',
          kind: 'dailyStreak',
          title: isNewStart ? 'Great start!' : 'Training streak!',
          message: isNewStart
            ? 'Great start! You completed today\'s first training.'
            : 'You have completed ' + streak + ' consecutive days of training!',
          iconSrc: getEndPracticeImage(),
          iconAlt: 'Cheerful trainer',
        }]);
      }
      const [completedNotes, { levelUp, achievements }] = await Promise.all([completedNotesPromise, postSessionPromise]);
      if (!cancelled && (completedNotes.length > 0 || levelUp || achievements.length > 0)) {
        setResultCelebrations((current) => [
          ...current,
          ...completedNotes,
          ...(levelUp ? [levelUp] : []),
          ...achievements,
        ]);
      }
    };
    void loadCelebrations();
    return () => { cancelled = true; };
  }, [sessionComplete, sessionResults]);

  const activeResultCelebration = resultCelebrations[0] || null;
  const closeResultCelebration = useCallback(() => {
    const id = activeResultCelebration?.id;
    setResultCelebrations((current) => id && current[0]?.id === id ? current.slice(1) : current);
  }, [activeResultCelebration?.id]);

  const handleSessionComplete = (results) => {
    if (sessionCompletionHandledRef.current) return;
    sessionCompletionHandledRef.current = true;
    const practicedCardCount = Number(results?.cardsCompleted ?? results?.cardsStudied ?? 0);
    if (practicedCardCount <= 0) {
      navigate(isQuickPractice ? quickReturnTo : '/', { state: isQuickPractice ? testContext?.returnState : undefined });
      return;
    }

    setSessionComplete(true);
    setSessionResults(results);
    setResultCelebrations([]);
    
    // Dispatch custom event to notify MainPage to refresh streak data
    // Practice Streak Calendar Requirements: 2.4, 7.4
    const event = new CustomEvent('practiceSessionComplete', {
      detail: {
        sessionResults: results,
        timestamp: new Date().toISOString()
      }
    });
    window.dispatchEvent(event);

    // Also persist session results so MainPage can pick them up on mount
    // (MainPage may not be mounted when this event fires)
    try {
      sessionStorage.setItem('pendingSessionResults', JSON.stringify(results));
    } catch (e) {
      // Ignore storage errors
    }
  };

  const handleBackToMap = async () => {
    if (!sessionComplete && practiceFlashCards.length > 0) {
      const confirmed = await confirm({
        title: 'Leave Practice?',
        message: "Repeats and grades you've already made will stay, but this practice session won't be saved.",
        confirmLabel: 'Leave',
        cancelLabel: 'Stay',
        tone: 'warning',
      });

      if (!confirmed) {
        return;
      }
    }

    navigate(isQuickPractice ? quickReturnTo : '/', { state: isQuickPractice ? testContext?.returnState : undefined });
  };

  if (practiceFlashCards.length === 0) {
    return (
      <NoCardsPracticeScene
        scope={testContext?.type === 'note' ? 'note' : 'all'}
        onContinue={() => navigate('/')}
      />
    );
  }

  if (!sessionComplete && practiceGateState.status === 'empty') {
    return (
      <NoCardsPracticeScene
        scope={testContext?.type === 'note' ? 'note' : 'all'}
        onContinue={() => navigate('/')}
      />
    );
  }

  if (!sessionComplete && practiceGateState.status === 'checking') {
    return (
      <div className="practice-view">
        <Spinner size="medium" message="Loading practice..." />
        {confirmDialog}
      </div>
    );
  }

  if (!sessionComplete && practiceGateState.status === 'coaching') {
    return (
      <CoachingTimeOverlay
        message={practiceGateState.coachingMessage}
        onContinue={continueAfterCoaching}
      />
    );
  }

  if (!sessionComplete && practiceGateState.status === 'opponent') {
    return (
      <TournamentOpponentIntro
        intro={practiceGateState.opponentIntro}
        onContinue={continueAfterOpponentIntro}
      />
    );
  }

  if (sessionComplete && sessionResults) {
    return (
      <div className="practice-view practice-result-view">
        <SessionSummary 
          sessionResults={sessionResults}
          entranceEnabled={!activeResultCelebration}
          actions={(
            <button onClick={handleBackToMap} className="btn-back btn-result-home">
              <Home size={18} strokeWidth={2.3} aria-hidden="true" />
              <span>{isQuickPractice ? t('quickPractice.back') : t('practicePlayer.page.home')}</span>
            </button>
          )}
        />
        <CelebrationPopup
          key={activeResultCelebration?.id || 'none'}
          {...activeResultCelebration}
          isVisible={Boolean(activeResultCelebration)}
          onClose={closeResultCelebration}
        />
      </div>
    );
  }

  return (
    <div className="practice-view">
      <PracticeSession
        onOpenWriting={() => navigate('/level-up')}
        flashCards={practiceFlashCards}
        onSessionComplete={handleSessionComplete}
        onFlashCardsUpdate={isQuickPractice ? undefined : onFlashCardsUpdate}
        settings={practiceSettings}
        practiceScope={testContext?.type === 'note' || isQuickPractice ? 'note' : 'all'}
        practiceStartId={practiceAccess?.practiceStartId || null}
        canUseTts={practiceAccess?.entitlements?.canUseTts !== false}
        infinityMode={practiceSettings.infinityMode || false}
        onRequestNextBatch={handleRequestNextBatch}
      />
      {confirmDialog}
    </div>
  );
}

export default App;
export { PracticeView, MapView, AppMain };
